import React, { useCallback, useRef } from 'react';

import { forwardInputRef, IInputProps } from '../types/input';
import { InputBase } from '../InputBase';
import { Icon } from '../Icon';
import { setInputElementValue } from '../utils/setInputElementValue';
import { useMergedRef } from '../utils/useMergedRef';

import s from './index.module.css';

interface ITextInputProps<TName extends string = string, TValue extends string = string>
  extends IInputProps<TName, TValue, HTMLInputElement> {
  startElem?: React.ReactElement;
  dataTestId?: string;
}

const TextInput = forwardInputRef(
  <TName extends string = string>(
    props: ITextInputProps<TName, string>,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const {
      value: inputValue,
      name,
      onChange,
      hasError,
      isDisabled,
      startElem,
      placeholder,
      dataTestId,
      onBlur,
    } = props;

    const inputMutableRef = useRef<HTMLInputElement>(null);
    const mergedRef = useMergedRef<HTMLInputElement>(ref, inputMutableRef);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event, {
          name,
          value: event.currentTarget.value,
        });
      },
      [name, onChange],
    );

    const handleClear = useCallback(() => {
      if (inputMutableRef.current) {
        setInputElementValue(inputMutableRef.current, '');
        inputMutableRef.current.focus();
      }
    }, [inputMutableRef]);

    return (
      <InputBase
        dataTestId={dataTestId}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={onBlur}
        name={name}
        disabled={isDisabled}
        hasError={hasError}
        type="text"
        ref={mergedRef}
        value={inputValue}
        startElement={startElem}
        endElement={
          inputValue !== '' &&
          !isDisabled && (
            <button
              tabIndex={-1}
              className={s.clearButton}
              onClick={handleClear}
              type="button"
              data-test-id="clear-button"
            >
              <Icon size="16px" icon={'x-circle-filled'} className={s.clearIcon} />
            </button>
          )
        }
      />
    );
  },
);

export type { ITextInputProps };
export { TextInput };
